import { ShieldCheck } from 'lucide-react'

// Data Protection & Confidentiality gate.
//
// Shown before a user reaches any screen carrying client spend data. The
// acknowledgement is deliberately NOT remembered across sessions — the whole
// point of the notice is that someone actively confirms it each time they
// sit down with the data, which is also what makes "log out when you've
// finished" a meaningful instruction rather than a one-off click.

export default function DataProtectionNotice({ accepted, onChange, compact = false }) {
  return (
    <div className={`text-left border border-gray-200 rounded-xl bg-gray-50/70 ${compact ? 'p-4' : 'p-5'}`}>
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-brand-dark shrink-0" />
        <h2 className={`font-semibold text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
          Data Protection &amp; Confidentiality
        </h2>
      </div>

      <div className={`space-y-2.5 text-gray-700 ${compact ? 'text-[11px] leading-relaxed' : 'text-xs leading-relaxed'}`}>
        <p>
          This application contains confidential and potentially commercially sensitive
          business information, intended solely for authorised users.
        </p>
        <p className="font-medium text-gray-800">
          By accessing this application, you acknowledge and agree to the following:
        </p>
        <ul className="space-y-2 list-none pl-0">
          <li className="flex gap-2">
            <span className="text-brand-dark shrink-0">&bull;</span>
            <span>
              Information must be handled securely and in accordance with applicable UK data
              protection legislation, including UK GDPR and the Data Protection Act 2018.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-dark shrink-0">&bull;</span>
            <span>
              Reports, financial information and other sensitive data must not be shared,
              copied, downloaded, photographed or distributed without appropriate authorisation.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-dark shrink-0">&bull;</span>
            <span>
              You must ensure that information displayed on your screen cannot be viewed by
              unauthorised individuals.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-dark shrink-0">&bull;</span>
            <span>
              <strong className="text-gray-900">
                When you have finished reviewing a report, you must close the report and log out
                of the application.
              </strong>{' '}
              Do not leave confidential information visible or accessible on an unattended device.
            </span>
          </li>
        </ul>
      </div>

      <label className="mt-4 pt-4 border-t border-gray-200 flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={accepted}
          onChange={e => onChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 shrink-0 accent-[color:var(--dp-accent)] cursor-pointer"
          style={{ '--dp-accent': 'rgb(var(--brand-dark))' }}
        />
        <span className={`font-medium text-gray-800 group-hover:text-gray-900 ${compact ? 'text-[11px] leading-snug' : 'text-xs leading-snug'}`}>
          I confirm that I have read and understood the Data Protection &amp; Confidentiality
          Notice and agree to handle all information responsibly and securely.
        </span>
      </label>
    </div>
  )
}
